import { useMemo } from "react";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { usePreQuote } from "./usePreQuote";
import { useQuote } from "./useQuote";
import { COUNTRY_CURRENCY } from "./useExchangeRate";
import type {
  IEstimateOption,
  IEstimateResult,
  IPreQuoteRequest,
  IQuoteResponse,
  QuoteAnchor,
} from "../services/api";
import {
  availableFundingCategories,
  availableFundingMethodsFromDiscovery,
  buildClaimFundingPreQuoteInput,
  buildPayoutDiscoveryPreQuoteInput,
  filterPreQuoteOptions,
  isFundingMethod,
  optionForCategory as pickOptionForCategory,
  optionForFundingMethod as pickOptionForFundingMethod,
  resolvePayerCode,
  resolvePayoutMethod,
  type FundingCategory,
  type FundingMethod,
} from "../utils/catalog";
import { paymentType } from "../interfaces/payment";
import { isAmountEditable, resolveAmountAnchor } from "../utils/amountAnchor";
import {
  isDestinationCurrencyEditable,
  isPayoutEditable,
} from "../utils/payoutAnchor";
import {
  useComposeStore,
  type ComposeSelection,
} from "../store/useComposeStore";
import {
  claimDefaultPayoutSelection,
  pinnablePayerCode,
  resolveComposePayerCode,
} from "../utils/claimPayoutCompose";
import { PayoutMethodType } from "../interfaces/payout";

export interface TransferEconomics {
  sendAmount: number;
  receiveAmount: number;
  exchangeRate: number;
  fee: number;
  totalCost: number;
  /** True while the numbers come from the indicative pre-quote (not binding). */
  estimated: boolean;
}

export interface TransferPricing {
  amountAnchor: QuoteAnchor;
  sourceCountry: string;
  destinationCountry: string;
  sourceCurrency: string;
  destinationCurrency: string;
  /** Canonical payout method fixed by the claim link (e.g. `CASH_PICKUP`). */
  payoutMethod: string | undefined;
  /** Canonical payer fixed by the claim link (e.g. `ELEKTRA`), if pinned. */
  payerCode: string | undefined;
  payoutEditable: boolean;
  destinationCurrencyEditable: boolean;
  composeSelection: ComposeSelection | null;
  allPayoutMethodsForPicker: PayoutMethodType[];
  /** Unpinned, one-per-payer options that feed the payout picker. */
  payoutDiscoveryOptions: IEstimateOption[];
  payoutDiscoveryLoading: boolean;
  /** Edited amount for the anchored leg. `config.amount` only seeds the input. */
  amount: number | null;

  preQuoteLoading: boolean;
  preQuoteError: Error | null;
  routeUnavailable: boolean;
  options: IEstimateOption[];
  availableCategories: Set<FundingCategory>;
  availableFundingMethods: FundingMethod[];
  optionForCategory: (category: FundingCategory) => IEstimateOption | undefined;
  bestOption: IEstimateOption | null;
  /** Pre-collapse rails; used to harvest limits corridors (D20). */
  facets: IEstimateResult["facets"];

  selectedOption: IEstimateOption | null;
  selectedFundingMethod: string | null;
  capabilityKey: string | null;

  quote: IQuoteResponse | null;
  quoteLoading: boolean;
  quoteError: Error | null;
  refetchQuote: () => void;
  isExpiringSoon: boolean;
  timeRemaining: number;

  economics: TransferEconomics | null;
}

export interface UseTransferPricingParams {
  /**
   * Load the unpinned payout discovery pre-quote. Only the payout picker needs
   * it, so the screen keeps one pre-quote per amount change while it is closed.
   */
  discoverPayouts?: boolean;
}

/**
 * Single source of truth for the fund-request quote screen pricing.
 */
export const useTransferPricing = (
  { discoverPayouts = false }: UseTransferPricingParams = {},
): TransferPricing => {
  const { amountState, paymentState, pendingFundingMethod } = useStore();
  const { session } = useSessionStore();

  const config = session.data?.config;
  const userData = session.data?.userData;

  const claimDefaultAnchor = resolveAmountAnchor(config);
  const amountEditable = isAmountEditable(config);
  const payoutEditable = isPayoutEditable(config);
  const destinationCurrencyEditable =
    isDestinationCurrencyEditable(config);
  const amountAnchor = amountEditable
    ? (amountState.anchor ?? claimDefaultAnchor)
    : claimDefaultAnchor;

  const storedDestinationCurrency = useComposeStore(
    (s) => s.destinationCurrency,
  );
  const storedSourceCurrency = useComposeStore((s) => s.sourceCurrency);
  const composeSelection = useComposeStore((s) => s.selection);

  const corridor = config?.defaultCorridor || config?.corridors?.[0];
  const sourceCountry = corridor?.originCountryCode || "US";
  const destinationCountry = corridor?.destinationCountryCode || "MX";
  const claimSourceCurrency =
    config?.sourceCurrency || COUNTRY_CURRENCY[sourceCountry] || "USD";
  const sourceCurrency = destinationCurrencyEditable
    ? (storedSourceCurrency ?? claimSourceCurrency)
    : claimSourceCurrency;
  const claimDestinationCurrency =
    config?.destinationCurrency ||
    COUNTRY_CURRENCY[destinationCountry] ||
    "MXN";
  const destinationCurrency = destinationCurrencyEditable
    ? (storedDestinationCurrency ?? claimDestinationCurrency)
    : claimDestinationCurrency;

  const effectiveSelection = payoutEditable
    ? (composeSelection ?? claimDefaultPayoutSelection(userData))
    : null;

  const payoutMethod = payoutEditable
    ? (effectiveSelection?.method ??
      resolvePayoutMethod(userData))
    : resolvePayoutMethod(userData);
  const payerCode = payoutEditable
    ? (resolveComposePayerCode(effectiveSelection) ??
      (effectiveSelection ? undefined : resolvePayerCode(userData)))
    : resolvePayerCode(userData);

  // What the pre-quote may pin. Differs from `payerCode` only for saved bank
  // rows, whose `bankId` is a display name rather than a catalog payer code.
  const pricingPayerCode = payoutEditable
    ? (effectiveSelection
      ? pinnablePayerCode(effectiveSelection)
      : resolvePayerCode(userData))
    : payerCode;

  const editableAmount = useMemo(() => {
    const raw = amountState.amount?.replace(/,/g, "");
    const parsed = raw ? parseFloat(raw) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [amountState.amount]);

  const lockedAmount = useMemo(() => {
    const pinned = config?.amount;
    return typeof pinned === "number" && Number.isFinite(pinned) && pinned > 0
      ? pinned
      : null;
  }, [config?.amount]);

  // Suggested claim amount seeds the input; pricing follows the edited leg.
  const amount = amountEditable ? editableAmount : lockedAmount;

  const quoteCurrency =
    amountAnchor === "receive" ? destinationCurrency : sourceCurrency;

  // Always priced against the destination the user is actually sending to —
  // the claim pin, or the active compose selection when payout is editable.
  // Pinning is what makes `vary: "fundingMethod"` fan out funding rails for
  // THAT payer; an unpinned call collapses to one payer per funding method.
  const preQuoteInput = useMemo<IPreQuoteRequest | null>(() => {
    if (!amount) return null;
    return buildClaimFundingPreQuoteInput({
      amount,
      amountAnchor,
      originCountryCode: sourceCountry,
      destinationCountryCode: destinationCountry,
      sourceCurrency,
      destinationCurrency,
      payoutMethod,
      payerCode: pricingPayerCode,
    });
  }, [
    amount,
    amountAnchor,
    sourceCountry,
    destinationCountry,
    sourceCurrency,
    destinationCurrency,
    payoutMethod,
    pricingPayerCode,
  ]);

  const {
    result: preQuote,
    loading: preQuoteLoading,
    error: preQuoteError,
  } = usePreQuote(preQuoteInput);

  const options = useMemo(
    () =>
      filterPreQuoteOptions(preQuote?.options, payoutMethod, pricingPayerCode),
    [preQuote?.options, payoutMethod, pricingPayerCode],
  );

  // Payout picker only: every payer/method the corridor offers, nothing pinned.
  const payoutDiscoveryEnabled = payoutEditable && discoverPayouts;
  const payoutDiscoveryInput = useMemo<IPreQuoteRequest | null>(() => {
    if (!payoutDiscoveryEnabled || !amount) return null;
    return buildPayoutDiscoveryPreQuoteInput({
      amount,
      amountAnchor,
      originCountryCode: sourceCountry,
      destinationCountryCode: destinationCountry,
      sourceCurrency,
      destinationCurrency,
    });
  }, [
    payoutDiscoveryEnabled,
    amount,
    amountAnchor,
    sourceCountry,
    destinationCountry,
    sourceCurrency,
    destinationCurrency,
  ]);

  const {
    result: payoutDiscovery,
    loading: payoutDiscoveryFetching,
  } = usePreQuote(payoutDiscoveryInput);

  const payoutDiscoveryLoading =
    payoutDiscoveryEnabled && (payoutDiscoveryFetching || !payoutDiscovery);

  const allPayoutMethodsForPicker = useMemo(() => {
    if (!payoutEditable) return [];
    const methods = new Set<PayoutMethodType>();
    for (const facet of payoutDiscovery?.facets?.payoutMethods ?? []) {
      if (
        Object.values(PayoutMethodType).includes(
          facet.payoutMethod as PayoutMethodType,
        )
      ) {
        methods.add(facet.payoutMethod as PayoutMethodType);
      }
    }
    for (const o of payoutDiscovery?.options ?? []) {
      if (o.payoutMethod === PayoutMethodType.BANK_ACCOUNT) {
        methods.add(PayoutMethodType.BANK_ACCOUNT);
      } else if (o.payoutMethod === PayoutMethodType.CASH_PICKUP) {
        methods.add(PayoutMethodType.CASH_PICKUP);
      } else if (o.payoutMethod === PayoutMethodType.PUSH_TO_CARD) {
        methods.add(PayoutMethodType.PUSH_TO_CARD);
      } else if (o.payoutMethod === PayoutMethodType.MOBILE_WALLET) {
        methods.add(PayoutMethodType.MOBILE_WALLET);
      }
    }
    return [...methods];
  }, [
    payoutEditable,
    payoutDiscovery?.facets?.payoutMethods,
    payoutDiscovery?.options,
  ]);

  // A refetch keeps the previous result until it resolves, so an in-flight
  // pre-quote must never be read as "no route for this destination".
  const routeUnavailable =
    !!preQuote &&
    !preQuoteLoading &&
    amount != null &&
    options.length === 0;

  const categories = useMemo(
    () => availableFundingCategories(options),
    [options]
  );

  const bestOption = useMemo<IEstimateOption | null>(() => {
    if (options.length === 0) return null;
    return [...options].sort((a, b) => a.sendAmount - b.sendAmount)[0];
  }, [options]);

  const optionForCategory = useMemo(
    () => (category: FundingCategory) => pickOptionForCategory(options, category),
    [options]
  );

  const fundingMethods = useMemo(
    () =>
      availableFundingMethodsFromDiscovery({
        facetFundingMethods: preQuote?.facets?.fundingMethods,
        options,
      }),
    [options, preQuote?.facets?.fundingMethods]
  );

  const selectedOption = useMemo<IEstimateOption | null>(() => {
    if (!isFundingMethod(pendingFundingMethod)) return null;
    return pickOptionForFundingMethod(options, pendingFundingMethod) ?? null;
  }, [options, pendingFundingMethod]);

  const selectedFundingMethod = selectedOption?.fundingMethod ?? null;
  const capabilityKey = selectedOption?.capabilityKey ?? null;

  const cardId =
    paymentState.payment?.type === paymentType.CARD
      ? paymentState.payment?.cardId ?? null
      : null;

  const {
    quote,
    loading: quoteLoading,
    error: quoteError,
    refetch: refetchQuote,
    isExpiringSoon,
    timeRemaining,
  } = useQuote({
    sourceCountry,
    destinationCountry,
    fundingMethod: selectedFundingMethod,
    payoutMethod: payoutMethod ?? null,
    capabilityKey,
    amount,
    currency: quoteCurrency,
    destinationCurrency,
    anchor: amountAnchor,
    cardId,
  });

  const economics = useMemo<TransferEconomics | null>(() => {
    if (quote) {
      return {
        sendAmount: quote.sendAmount,
        receiveAmount: quote.receiveAmount,
        exchangeRate: quote.exchangeRate,
        fee: quote.totalFee ?? quote.fee ?? 0,
        totalCost: quote.totalCost,
        estimated: false,
      };
    }
    const option = selectedOption ?? bestOption;
    if (option) {
      return {
        sendAmount: option.sendAmount,
        receiveAmount: option.receiveAmount,
        exchangeRate: option.customerFxRate,
        fee: option.customerFee,
        totalCost: option.sendAmount + option.customerFee,
        estimated: true,
      };
    }
    return null;
  }, [quote, selectedOption, bestOption]);

  return {
    amountAnchor,
    sourceCountry,
    destinationCountry,
    sourceCurrency,
    destinationCurrency,
    payoutMethod,
    payerCode,
    payoutEditable,
    destinationCurrencyEditable,
    composeSelection: effectiveSelection,
    allPayoutMethodsForPicker,
    payoutDiscoveryOptions: payoutDiscovery?.options ?? [],
    payoutDiscoveryLoading,
    amount,
    preQuoteLoading,
    preQuoteError,
    routeUnavailable,
    options,
    availableCategories: categories,
    availableFundingMethods: fundingMethods,
    optionForCategory,
    bestOption,
    facets: preQuote?.facets,
    selectedOption,
    selectedFundingMethod,
    capabilityKey,
    quote,
    quoteLoading,
    quoteError,
    refetchQuote,
    isExpiringSoon,
    timeRemaining,
    economics,
  };
};
